'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '@/lib/api';
import { ackPhaseFromStatus,
  isConfirmedInFlightStatus, NO_ACK_DETAIL, type AckPhase } from '@/lib/command-ack';
import type { DockAckPayload, DockCommand } from '@/types/dock';

/**
 * Sends dock commands and tracks their acks.
 *
 * The AirScort ack contract only guarantees `command_id`; the adapter stamps the
 * last published command name onto acks that lack one (see enrichAckPayload), so
 * a pending entry resolves on EITHER identifier. A pending entry that is never
 * acked does NOT vanish: its window elapses into a failed "No acknowledgement
 * received" state, so an unconfirmed command is never mistaken for a successful
 * one (that is the fire-and-forget failure #47 exists to prevent).
 */

interface CommandAckState {
  commandId: string;
  phase: AckPhase;
  detail: string | null;
}

const ACK_TERMINAL_CLEAR_MS = 5_000;
const ACK_PENDING_EXPIRE_MS = 15_000;

export interface DockCommandController {
  /** Command currently being POSTed (one at a time). */
  submitting: DockCommand | null;
  /** Ack phase for a command, or null when nothing is in flight for it. */
  phaseOf: (cmd: DockCommand) => AckPhase | null;
  /** Detail text for a failed ack, or null. */
  failureOf: (cmd: DockCommand) => string | null;
  /** All commands whose ack came back failed, with detail. */
  failures: Array<[DockCommand, string]>;
  send: (cmd: DockCommand) => Promise<void>;
}

export function useDockCommand({
  dockId,
  subscribeDockAck,
  enabled = true,
}: {
  dockId: string;
  subscribeDockAck: (cb: (ack: DockAckPayload) => void) => () => void;
  /** False for viewers / offline docks — send() becomes a no-op. */
  enabled?: boolean;
}): DockCommandController {
  const [submitting, setSubmitting] = useState<DockCommand | null>(null);
  const [ackState, setAckState] = useState<Record<string, CommandAckState>>({});
  const ackStateRef = useRef(ackState);
  useEffect(() => {
    ackStateRef.current = ackState;
  }, [ackState]);

  const clearTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const scheduleAckClear = useCallback((cmd: string, commandId: string, delayMs: number) => {
    const timers = clearTimersRef.current;
    const existing = timers.get(cmd);
    if (existing) clearTimeout(existing);
    timers.set(
      cmd,
      setTimeout(() => {
        timers.delete(cmd);
        setAckState((prev) => {
          if (prev[cmd]?.commandId !== commandId) return prev;
          return Object.fromEntries(Object.entries(prev).filter(([key]) => key !== cmd));
        });
      }, delayMs),
    );
  }, []);

  // The pending/acked window for a command elapsed with no TERMINAL ack. Any ack
  // (done/failed/intermediate) replaces this timer via scheduleAck*, so if it
  // fires the command is unconfirmed by construction. Surface it as failed
  // ("No acknowledgement received") instead of deleting it silently - a vanished
  // command reads as success, the fire-and-forget hazard #47 targets - then clear
  // it like any terminal ack. ponytail: ACK_PENDING_EXPIRE_MS is the
  // confirmation-timeout knob; widen it if the real AirScort firmware acks slower.
  const scheduleAckExpire = useCallback((cmd: string, commandId: string, delayMs: number) => {
    const timers = clearTimersRef.current;
    const existing = timers.get(cmd);
    if (existing) clearTimeout(existing);
    timers.set(
      cmd,
      setTimeout(() => {
        timers.delete(cmd);
        setAckState((prev) => {
          if (prev[cmd]?.commandId !== commandId) return prev;
          return { ...prev, [cmd]: { ...prev[cmd], phase: 'failed', detail: NO_ACK_DETAIL } };
        });
        scheduleAckClear(cmd, commandId, ACK_TERMINAL_CLEAR_MS);
      }, delayMs),
    );
  }, [scheduleAckClear]);

  useEffect(() => {
    const timers = clearTimersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  useEffect(() => {
    return subscribeDockAck((ack) => {
      if (ack.dock_id !== dockId) return;
      const match = Object.entries(ackStateRef.current).find(
        ([cmd, st]) =>
          (typeof ack.command_id === 'string' && st.commandId === ack.command_id) ||
          (typeof ack.command === 'string' && ack.command === cmd),
      );
      if (!match) return;
      const [cmd, st] = match;
      const phase = ackPhaseFromStatus(typeof ack.status === 'string' ? ack.status : null);
      const detail = typeof ack.detail === 'string' ? ack.detail : st.detail;
      setAckState((prev) => ({ ...prev, [cmd]: { ...st, phase, detail } }));
      if (phase !== 'acked') {
        scheduleAckClear(cmd, st.commandId, ACK_TERMINAL_CLEAR_MS);
      } else if (isConfirmedInFlightStatus(ack.status)) {
        // `accepted`: the dock DID acknowledge, and it is the only success word
        // the real board can ever say. Its serial link is a one-way
        // opportunistic echo, so the Pi fork publishes `accepted` and nothing
        // further - there is no completion signal to wait for. Expiring this
        // into "No acknowledgement received" painted every WORKING command red
        // 15 s later, with the tray physically open in front of the operator.
        // Same defect, and same fix, as useDroneCommand's land/return_home.
        // Retire the readout without claiming an outcome.
        scheduleAckClear(cmd, st.commandId, ACK_PENDING_EXPIRE_MS);
      } else {
        // Nothing confirmed anything yet. Silence here IS a missing ack.
        scheduleAckExpire(cmd, st.commandId, ACK_PENDING_EXPIRE_MS);
      }
    });
  }, [subscribeDockAck, dockId, scheduleAckClear, scheduleAckExpire]);

  const send = useCallback(
    async (cmd: DockCommand) => {
      if (!enabled) return;
      setSubmitting(cmd);
      try {
        const res = await api.post<{ data: { command_id: string } }>(
          `/api/docks/${dockId}/commands`,
          { command: cmd },
        );
        setAckState((prev) => ({
          ...prev,
          [cmd]: { commandId: res.data.command_id, phase: 'pending', detail: null },
        }));
        scheduleAckExpire(cmd, res.data.command_id, ACK_PENDING_EXPIRE_MS);
        toast.success(`Command sent: ${cmd.replace(/_/g, ' ').toUpperCase()}`);
      } catch (err) {
        toast.error(getApiErrorMessage(err, 'Failed to send command'));
      } finally {
        setSubmitting(null);
      }
    },
    [dockId, enabled, scheduleAckExpire],
  );

  const phaseOf = useCallback((cmd: DockCommand) => ackState[cmd]?.phase ?? null, [ackState]);
  const failureOf = useCallback(
    (cmd: DockCommand) => {
      const st = ackState[cmd];
      return st?.phase === 'failed' ? st.detail : null;
    },
    [ackState],
  );
  const failures = Object.entries(ackState)
    .filter(([, st]) => st.phase === 'failed' && st.detail !== null)
    .map(([cmd, st]) => [cmd as DockCommand, st.detail as string] as [DockCommand, string]);

  return { submitting, phaseOf, failureOf, failures, send };
}
